import { t } from "ttag";

import { DURATION_IN_MONTHS } from "@/features/billing/models";
import { useOnboardingStore } from "@/features/onboarding/stores";
import type { CheckoutDiscount } from "@/features/payments/api";
import { PRICE_TYPE } from "@/shared/domains";
import { DISCOUNT_DURATION } from "@/shared/domains/coupons/enums";
import { PUERTO_RICO_STATE_VALUE } from "@/shared/models";
import { Alert, Heading } from "@/shared/ui";

const getDiscountDisclaimer = (checkoutDiscount?: CheckoutDiscount) => {
  if (!checkoutDiscount?.isValid) {
    return null;
  }

  const firstPriceWithDiscount = checkoutDiscount.prices.find(
    (price) => price.discount,
  );
  const discount = firstPriceWithDiscount?.discount;

  if (!discount?.isValid) {
    return null;
  }

  return {
    [DISCOUNT_DURATION.ONCE]: t`The discount will be applied to your first payment only`,
    [DISCOUNT_DURATION.REPEATING]: t`The discount will be applied to your payments for ${discount.durationInMonths} months`,
    [DISCOUNT_DURATION.FOREVER]: t`The discount will be applied to all payments until you cancel`,
  }[discount.duration];
};

interface Props {
  className?: string;
}

export const PaymentDetails = ({ className }: Props) => {
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const checkoutDiscount = useOnboardingStore(
    (state) => state.checkoutDiscount,
  );
  const isFromPuertoRico = useOnboardingStore(
    (state) => state.userAttributes?.state === PUERTO_RICO_STATE_VALUE,
  );

  const isQuarterlyShippingFrequency =
    isFromPuertoRico &&
    selectedCategories.some(
      (category) =>
        category.selectedProduct?.selectedPrice?.durationInMonths ===
        DURATION_IN_MONTHS.QUARTERLY,
    );

  const hasSomeRecurringPrice = selectedCategories.some(
    (category) =>
      category.selectedProduct?.selectedPrice?.type === PRICE_TYPE.RECURRING,
  );

  const shippingCadence = isQuarterlyShippingFrequency
    ? t`Three month plans shipped at once`
    : t`Medications ship monthly`;

  return (
    <Alert variant="outlined" withIcon={false} className={className}>
      <Heading level={3} color="primary" size="xx-small">
        {t`By selecting “Pay”, you agree to the following:`}
      </Heading>

      <ul className="list-inside list-disc pl-2">
        {selectedCategories.some((category) =>
          checkoutDiscount?.prices.some(
            (price) =>
              price.subscriptionPriceId ===
                category.selectedProduct?.selectedPrice?.subscriptionPriceId &&
              price.discount,
          ),
        ) && <li>{getDiscountDisclaimer(checkoutDiscount)}</li>}
        <li>{t`Charges will appear as "OpenLoop Healthcare Partners PC" on your bank statement`}</li>
        <li>{t`No refunds or credits for partial plan periods are given unless required by law`}</li>
        {hasSomeRecurringPrice && (
          <li>{t`Cancel anytime to stop future billing`}</li>
        )}
        <li>{t`If you pay for a treatment and your provider determines that you are ineligible, we will process a full refund`}</li>
        {hasSomeRecurringPrice && (
          <li>
            {t`Plans offer discounts for longer commitments. ${shippingCadence}. Early cancellations are refunded at the standard monthly rate`}
          </li>
        )}
      </ul>
    </Alert>
  );
};
